<template>
	<div class="box">
		<LoadingSpinner v-if="!loaded" />

		<template v-else>
			<div v-if="available_actions.length" class="mb-4">
				<h4>{{ $t('invoice.supplier_lifecycle_actions') }}</h4>
				<button
					v-for="action in available_actions"
					:key="action.code"
					class="btn mr-2 mb-2"
					:class="action.code === CODE_REFUSED ? 'btn-outline-danger' : 'btn-outline-primary'"
					@click="openAction(action.code)"
				>
					{{ $t('invoice.supplier_lifecycle_status.' + action.code) }}
				</button>
			</div>

			<p v-if="!steps.length" class="mb-0">{{ $t('invoice.supplier_lifecycle_empty') }}</p>

			<ul v-else class="invoice-lifecycle">
				<li v-for="step in steps" :key="step.key" class="invoice-lifecycle__step">
					<div class="invoice-lifecycle__title">
						{{ $t('invoice.supplier_lifecycle_status.' + step.code) }}
						<b-badge variant="light" class="ml-1">{{ step.code }}</b-badge>
						<b-badge v-if="step.mandatory" variant="light" class="ml-1 font-weight-normal">
							{{ $t('invoice.supplier_lifecycle_mandatory') }}
						</b-badge>
						<b-badge v-if="step.waiting" variant="warning" class="ml-1 font-weight-normal">
							{{ $t('invoice.supplier_lifecycle_publishing') }}
						</b-badge>
					</div>

					<div class="invoice-lifecycle__actor">{{ step.actor }}</div>

					<div v-if="step.occurred_at" class="invoice-lifecycle__actor">{{ formatDate(step.occurred_at, true) }}</div>

					<div v-if="step.reason" class="invoice-lifecycle__actor">
						{{ $t('invoice.supplier_lifecycle_reason') }} : {{ step.reason }}
					</div>

					<div v-if="step.amount" class="invoice-lifecycle__actor">
						{{ $t('invoice.supplier_lifecycle_amount') }} : {{ step.amount }}<template v-if="step.amount_remaining"> — {{ $t('invoice.supplier_lifecycle_amount_remaining') }} : {{ step.amount_remaining }}</template>
					</div>
				</li>
			</ul>

			<p v-if="is_terminal" class="text-muted mt-3 mb-0">
				{{ $t('invoice.supplier_lifecycle_closed') }}
			</p>
		</template>

		<b-modal ref="modal_status" hide-footer>
			<template v-slot:modal-title>
				{{ pending_code ? $t('invoice.supplier_lifecycle_status.' + pending_code) : '' }}
			</template>

			<div v-if="is_refusal" class="alert alert-danger">
				{{ $t('invoice.supplier_lifecycle_refuse_warning') }}
			</div>

			<template v-if="needs_reason">
				<div class="form-group">
					<label>{{ $t('invoice.supplier_lifecycle_reason_select') }} *</label>
					<e-select
						v-model="reason"
						:options="reasons"
						:loading="loading_reasons"
						track-by="lifecyclereason_code"
						label="lifecyclereason_label"
						:allow-empty="false"
						:show-labels="false"
					/>
					<small v-if="!loading_reasons && !reasons.length" class="text-danger">
						{{ $t('invoice.supplier_lifecycle_no_reason') }}
					</small>
				</div>

				<div class="form-group">
					<label>{{ $t('invoice.supplier_lifecycle_comment') }}<template v-if="comment_required"> *</template></label>
					<textarea v-model="comment" class="form-control" rows="3" maxlength="1000"></textarea>
					<small v-if="comment_required" class="text-muted">{{ $t('invoice.supplier_lifecycle_comment_required') }}</small>
				</div>
			</template>

			<div class="text-center">
				<button class="btn" :class="is_refusal ? 'btn-danger' : 'btn-primary'" :disabled="processing || !can_submit" @click="submitStatus">
					{{ $t('invoice.supplier_lifecycle_confirm') }}
					<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin />
				</button>
			</div>
		</b-modal>
	</div>
</template>


<script type="text/javascript">
	import Invoice from "@/mixins/Invoice.js"

	// Statuts que la plateforme transmet au PPF, et qui sont à ce titre obligatoires (XP Z12-012 §5.1).
	const MANDATORY_CODES = ['200', '210', '212', '213']

	// Statuts posés par les plateformes le long de l'acheminement.
	const PLATFORM_CODES = ['200', '201', '202', '203', '213']

	// Statuts que l'acheteur pose lui-même sur une facture qu'il a reçue.
	const BUYER_CODES = ['204', '205', '206', '207', '208', '210', '211']

	// Statuts proposés à l'utilisateur. Deux en sont exclus parce qu'ils découlent d'un geste métier
	// et n'ont pas à être posés à la main depuis cet écran : le 204 « Prise en charge », déclaré au
	// contrôle de la facture, et le 211 « Paiement transmis », déclaré à la saisie d'un règlement.
	const ACTION_CODES = ['205', '206', '207', '208', '210']

	// Statuts exigeant un motif codifié, tels que la norme les définit (BR-FR-CDV-15).
	const REASON_REQUIRED_CODES = ['206', '207', '208', '210']

	const CODE_REFUSED = '210'

	// Message décidé mais pas encore déposé sur la plateforme.
	const STATE_PENDING = 'PENDING'

	export default {
		name: "InvoiceSupplierFicheLifecycle",

		mixins: [Invoice],

		props: {
			invoice: { type: Object, default: () => ({}) },
			invoicesupplier_id: { type: [String, Number], default: null }
		},

		data() {
			return {
				CODE_REFUSED,
				loaded: false,
				timeline: null,
				pending_code: null,
				reasons: [],
				reason: null,
				comment: '',
				loading_reasons: false,
				processing: false
			}
		},

		computed: {
			is_terminal() {
				return !!(this.timeline && this.timeline.is_terminal)
			},

			/**
			 * Un cycle clos n'accepte plus rien, et un statut déjà en cours de transmission ne doit pas
			 * être reproposé : le serveur le refuserait, autant ne pas l'offrir.
			 */
			available_actions() {
				if(!this.timeline || this.is_terminal) {
					return []
				}

				const queued = (this.timeline.pending_outbox || []).map(message => message.code)

				return ACTION_CODES.filter(code => !queued.includes(code)).map(code => ({ code }))
			},

			needs_reason() {
				return REASON_REQUIRED_CODES.includes(this.pending_code)
			},

			is_refusal() {
				return this.pending_code === CODE_REFUSED
			},

			// Certains motifs n'ont de sens qu'accompagnés d'une explication : le référentiel le dit.
			comment_required() {
				return !!(this.reason && this.reason.lifecyclereason_requires_text)
			},

			can_submit() {
				if(!this.needs_reason) {
					return true
				}

				return !!this.reason && (!this.comment_required || !!this.comment.trim())
			},

			/**
			 * Frise du cycle de vie : les seuls évènements réellement survenus. Aucune projection des
			 * statuts à venir — la norme n'impose pas d'ordre entre statuts de traitement, et une
			 * prévision ne se distinguerait pas assez d'un fait pour un lecteur pressé.
			 */
			steps() {
				if(!this.timeline) {
					return []
				}

				// Seuls les messages encore à déposer sont signalés : une fois acceptés par la
				// plateforme, ils n'attendent plus qu'un acquittement, qui ne regarde pas l'utilisateur.
				// Le rattachement se fait sur l'évènement et non sur le code : une facture peut porter
				// plusieurs statuts identiques — un paiement puis sa rectification — dont un seul est
				// en attente.
				const waiting = (this.timeline.pending_outbox || [])
					.filter(message => message.state === STATE_PENDING)
					.map(message => message.event)

				return (this.timeline.events || []).map((event, index) => ({
					key: 'event_' + index,
					code: event.code,
					mandatory: MANDATORY_CODES.includes(event.code),
					waiting: waiting.includes(event.id),
					occurred_at: event.occurred_at,
					reason: this.buildReason(event),
					amount: event.amount_format,
					amount_remaining: event.amount_remaining_format,
					actor: this.buildActor(event)
				}))
			}
		},

		mounted() {
			// Une facture déposée à la main n'a pas de cycle de vie : l'onglet est grisé, mais son
			// adresse reste atteignable.
			if(this.invoice.suppliersocr_manual) {
				this.$router.replace({ name: 'invoiceSupplierFicheOverview', params: { invoicesupplier_id: this.invoice.suppliersocr_id } })
				return
			}

			this.init_component()
		},

		methods: {
			async init_component() {
				this.loaded = false

				const timeline = await this.getInvoiceLifecycle(this.invoice.suppliersocr_id)

				// Les montants sont mis en forme ici plutôt que dans la frise : leur formatage dépend de
				// la devise, chargée de façon asynchrone, et un computed rendrait des promesses.
				if(timeline) {
					const currency = this.invoice.suppliersocr_currency || 'EUR'

					for(const event of timeline.events || []) {
						event.amount_format = event.amount !== null ? await this.priceFormat(event.amount, currency, true) : null
						event.amount_remaining_format = event.amount_remaining !== null ? await this.priceFormat(event.amount_remaining, currency, true) : null
					}
				}

				this.timeline = timeline
				this.loaded = true
			},

			/**
			 * Ouvre la saisie d'un statut. Les motifs sont chargés à l'ouverture plutôt qu'au montage :
			 * ils dépendent du statut visé, et la plupart des consultations n'en ont pas besoin.
			 */
			async openAction(code) {
				this.pending_code = code
				this.reason = null
				this.comment = ''
				this.reasons = []
				this.$refs.modal_status.show()

				if(!REASON_REQUIRED_CODES.includes(code)) {
					return
				}

				this.loading_reasons = true
				this.reasons = await this.getLifecycleReasons(code)
				this.loading_reasons = false
			},

			/**
			 * Envoie la décision. Le refus porte la confirmation explicite qu'exige le serveur : elle
			 * matérialise le passage par cette modale et son avertissement.
			 */
			async submitStatus() {
				this.processing = true

				const result = await this.setInvoiceLifecycleStatus(this.invoice.suppliersocr_id, {
					code: this.pending_code,
					reason_code: this.reason ? this.reason.lifecyclereason_code : null,
					reason_text: this.comment ? this.comment.trim() : null,
					confirmed: this.is_refusal
				})

				this.processing = false

				// Un refus du serveur a déjà été signalé par la couche HTTP : la modale reste ouverte
				// pour que la saisie ne soit pas perdue.
				if(!result) {
					return
				}

				this.$refs.modal_status.hide()
				this.successToast('invoice.supplier_lifecycle_sent')

				await this.init_component()
				// Le bandeau porte le statut courant : il doit suivre la décision qui vient d'être prise.
				this.$emit('invoice-updated')
			},


			/**
			 * Qui a posé un statut déjà survenu. Le code renseigne mieux que l'acteur technique :
			 * un statut d'acheminement vient toujours d'une plateforme, quel que soit le compte qui
			 * l'a déposé.
			 */
			buildActor(event) {
				if(PLATFORM_CODES.includes(event.code)) {
					return this.$t('invoice.supplier_lifecycle_actor_platform')
				}

				if(!BUYER_CODES.includes(event.code)) {
					return this.$t('invoice.supplier_lifecycle_actor_supplier')
				}

				if(event.actor_name) {
					return event.actor_name
				}

				return this.$t(event.actor_type === 'SYSTEM' ? 'invoice.supplier_lifecycle_actor_auto' : 'invoice.supplier_lifecycle_actor_you')
			},

			/**
			 * Le motif d'un statut, tel qu'il se lit : le libellé du référentiel plutôt que son code,
			 * suivi du commentaire libre s'il y en a un. Le code ne ressort que si le motif a disparu
			 * du référentiel depuis son émission.
			 */
			buildReason(event) {
				return [event.reason_label || event.reason_code, event.reason_text].filter(part => part).join(' — ')
			}
		},

		components: {
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
		}
}
</script>
